import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { action, makeObservable, observable } from "mobx";
import type { AmdRetryRow, AmdRow } from "models/AutoRequeueModels";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import { PipelineService } from "../../../../../services/PipelineService";
import {AcxStore, type IRootStore} from "stores/RootStore";

export type PageDirection = "first" | "next" | "prev";

type RetriedCursor = { lastRetriedOn: string; lastId: string };
type ProcessingCursor = { lastArrivedOn: string; lastId: string };

@AcxStore
export default class AutoRequeueStore extends BaseStore {
    readonly authStore: AuthStore;
    readonly pipelineService: PipelineService;

    @observable public requeuedAmds: AmdRetryRow[] = [];
    @observable public processingAmds: AmdRow[] = [];

    @observable public take: number = 50;
    @observable public minHoursAgoRetried: number = 3;
    @observable public minHoursAgoProcessing: number = 1;
    @observable public minRetryCount: number = 1;

    // Retried AMDs pagination
    @observable public requeuedAmdsPage: number = 1;
    @observable public requeuedAmdsHasMore: boolean = false;
    @observable public requeuedAmdsHasPrev: boolean = false;
    @observable public requeuedLastCursors: RetriedCursor[] = [];
    @observable public requeuedNextCursor: RetriedCursor | undefined = undefined;

    // Processing AMDs pagination
    @observable public processingAmdsPage: number = 1;
    @observable public processingAmdsHasMore: boolean = false;
    @observable public processingAmdsHasPrev: boolean = false;
    @observable public processingLastCursors: ProcessingCursor[] = [];
    @observable public processingNextCursor: ProcessingCursor | undefined = undefined;

    readonly requeuedAmdsDgStore: AcxDataGridStore;
    readonly processingAmdsDgStore: AcxDataGridStore;

    constructor(public rootStore: IRootStore) {
        super("AutoRequeueStore");
        makeObservable(this);

        this.requeuedAmdsDgStore = new AcxDataGridStore(
            "RequeuedAmds",
            "Administrative",
        );
        this.requeuedAmdsDgStore.checkboxSelection = false;

        this.processingAmdsDgStore = new AcxDataGridStore(
            "ProcessingAmds",
            "Administrative",
        );
        this.processingAmdsDgStore.checkboxSelection = false;

        this.pipelineService = new PipelineService();
        this.authStore = rootStore.getStore(AuthStore);
    }

    @action setTake(take: number) { this.take = take; }
    @action setMinHoursAgoRetried(hours: number) { this.minHoursAgoRetried = hours; }
    @action setMinHoursAgoProcessing(hours: number) { this.minHoursAgoProcessing = hours; }
    @action setMinRetryCount(count: number) { this.minRetryCount = count; }

    public async loadRequeuedAmds(direction: PageDirection = "first") {
        this.requeuedAmdsDgStore.isLoading = true;

        let cursor: RetriedCursor | undefined;
        if (direction === "first") {
            this.requeuedLastCursors = [];
            this.requeuedAmdsPage = 1;
            cursor = undefined;
        } else if (direction === "next") {
            this.requeuedLastCursors.push(this.requeuedNextCursor!);
            this.requeuedAmdsPage += 1;
            cursor = this.requeuedNextCursor;
        } else if (direction === "prev") {
            this.requeuedLastCursors.pop();
            this.requeuedAmdsPage -= 1;
            cursor = this.requeuedLastCursors[this.requeuedLastCursors.length - 1];
        }

        try {
            const results = await this.pipelineService.getRequeuedAMDs(
                this.take,
                this.minHoursAgoRetried,
                this.minRetryCount,
                cursor?.lastRetriedOn,
                cursor?.lastId,
            );
            this.requeuedAmds = results;
            this.requeuedAmdsDgStore.rows = results;
            this.requeuedAmdsHasMore = results.length === this.take;
            this.requeuedAmdsHasPrev = this.requeuedLastCursors.length > 0;
            const last = results[results.length - 1];
            this.requeuedNextCursor = last
                ? { lastRetriedOn: last.lastRetriedOn, lastId: last.id }
                : undefined;
        } finally {
            this.requeuedAmdsDgStore.isLoading = false;
        }
    }

    public async loadProcessingAmds(direction: PageDirection = "first") {
        this.processingAmdsDgStore.isLoading = true;

        let cursor: ProcessingCursor | undefined;
        if (direction === "first") {
            this.processingLastCursors = [];
            this.processingAmdsPage = 1;
            cursor = undefined;
        } else if (direction === "next") {
            this.processingLastCursors.push(this.processingNextCursor!);
            this.processingAmdsPage += 1;
            cursor = this.processingNextCursor;
        } else {
            this.processingLastCursors.pop();
            this.processingAmdsPage -= 1;
            cursor = this.processingLastCursors[this.processingLastCursors.length - 1];
        }

        try {
            const results = await this.pipelineService.getProcessingAMDs(
                this.take,
                this.minHoursAgoProcessing,
                cursor?.lastArrivedOn,
                cursor?.lastId,
            );
            this.processingAmds = results;
            this.processingAmdsDgStore.rows = results;
            this.processingAmdsHasMore = results.length === this.take;
            this.processingAmdsHasPrev = this.processingLastCursors.length > 0;
            const last = results[results.length - 1];
            this.processingNextCursor = last
                ? { lastArrivedOn: last.arrivedOn, lastId: last.id }
                : undefined;
        } finally {
            this.processingAmdsDgStore.isLoading = false;
        }
    }

    public async loadAll() {
        await Promise.all([this.loadRequeuedAmds("first"), this.loadProcessingAmds("first")]);
    }
}
